import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { formatDate, formatDateTime, getDateInputValue, getTodayIST, addDaysIST } from '../utils/dateUtils';
import { FiCalendar, FiClock, FiUser, FiUsers, FiPhone, FiPlus } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import BookingForm from '../components/BookingForm';
import { getServicesForDepartment, getDepartmentStaffDepartmentForBookingCategory, normalizeCategory } from '../utils/departmentMapping';
import InteractiveCalendar from '../components/ui/InteractiveCalendar';

function Bookings() {
  const { user } = useAuth();
  // Fixed list — only these 3 services support slot-based bookings in the dashboard
  // useMemo with no deps gives a stable reference that never changes
  const allServices = useMemo(() => ['Spa', 'Yoga', 'Salon'], []);

  const [bookings, setBookings] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getTodayIST());
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [slotSettings, setSlotSettings] = useState({ timeSlots: {}, workingHours: null });
  const [departmentStaff, setDepartmentStaff] = useState([]);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editForm, setEditForm] = useState({ booking_date: '', booking_time: '', work_done_by: '' });
  const [editSlots, setEditSlots] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingBookingId, setDeletingBookingId] = useState(null);

  // Filter services based on user role, allowed_activities, and department (Memoized to prevent infinite re-render loop)
  const SERVICES = useMemo(() => {
    // Admin and manager see all
    if (user?.role === 'admin' || user?.role === 'manager') {
      return allServices;
    }

    // 1. Check custom allowed_activities from Security / Users page
    if (Array.isArray(user?.allowed_activities) && user.allowed_activities.length > 0) {
      const allowedActivities = user.allowed_activities
        .map(a => normalizeCategory(a) || a)
        .filter(Boolean);

      const filtered = allServices.filter(service => {
        const norm = normalizeCategory(service);
        return allowedActivities.some(a => normalizeCategory(a) === norm || String(a).toLowerCase() === String(service).toLowerCase());
      });

      if (filtered.length > 0) {
        return filtered;
      }
      return allowedActivities;
    }

    // 2. Department users see their assigned department services
    if (user?.department) {
      const userServices = getServicesForDepartment(user.department);
      const filtered = allServices.filter(service => {
        const norm = normalizeCategory(service);
        return userServices.some(us => normalizeCategory(us) === norm || String(us).toLowerCase() === String(service).toLowerCase());
      });
      if (filtered.length > 0) {
        return filtered;
      }
      return userServices.length > 0 ? userServices : [normalizeCategory(user.department) || user.department];
    }

    // 3. Fallback for any other user role with access to /bookings
    return allServices;
  }, [user?.role, user?.department, user?.allowed_activities]);

  const isDepartmentUser = user?.role === 'department' || Boolean(user?.department) || (Array.isArray(user?.allowed_activities) && user.allowed_activities.length > 0);
  const shouldLockBookingService = isDepartmentUser && SERVICES.length === 1;
  const upcomingDays = Array.from({ length: 7 }, (_, index) => addDaysIST(getTodayIST(), index));

  useEffect(() => {
    if (SERVICES.length > 0) {
      if (!selectedService || !SERVICES.includes(selectedService)) {
        setSelectedService(SERVICES[0]);
      }
    }
  }, [SERVICES, selectedService]);

  const fetchBookings = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      // If no services available, return empty
      if (SERVICES.length === 0) {
        setBookings([]);
        return;
      }
      
      // Fetch all requests and filter client-side for bookings
      let url = '/api/roomservice/requests';
      if (selectedService) {
        url += `?category=${encodeURIComponent(selectedService)}`;
      }
      
      const response = await api.get(url);
      const rows = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.data) ? response.data.data : []);
      
      // Filter to only show bookings with date/time (slot-based bookings)
      const slotBookings = rows.filter(req => 
        req.metadata?.booking_date && 
        req.metadata?.booking_time &&
        (!selectedService || req.category === selectedService || normalizeCategory(req.category) === normalizeCategory(selectedService)) &&
        ['pending', 'received', 'in-progress', 'completed'].includes(req.status)
      );
      
      // Sort by date and time
      slotBookings.sort((a, b) => {
        const dateA = a.metadata.booking_date;
        const dateB = b.metadata.booking_date;
        if (dateA !== dateB) {
          return dateA.localeCompare(dateB);
        }
        return String(a.metadata.booking_time || '').localeCompare(String(b.metadata.booking_time || ''));
      });
      
      setBookings(slotBookings);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [selectedService, SERVICES]);

  const fetchSlotSettings = useCallback(async () => {
    if (!selectedService) {
      setSlotSettings({ timeSlots: {}, workingHours: null });
      return;
    }

    try {
      const canonicalService = normalizeCategory(selectedService) || selectedService;
      const response = await api.get(`/api/slots/${encodeURIComponent(canonicalService)}`);
      setSlotSettings({
        timeSlots: response.data?.timeSlots || {},
        workingHours: response.data?.workingHours || null
      });
    } catch (error) {
      console.error('Failed to fetch slot settings:', error);
      setSlotSettings({ timeSlots: {}, workingHours: null });
    }
  }, [selectedService]);

  const fetchDepartmentStaff = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/staff?is_active=true');
      setDepartmentStaff(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch department staff:', error);
      setDepartmentStaff([]);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    // Refresh every 30 seconds silently
    const interval = setInterval(() => fetchBookings(true), 30000);
    return () => clearInterval(interval);
  }, [fetchBookings]);

  useEffect(() => {
    fetchSlotSettings();
  }, [fetchSlotSettings]);

  useEffect(() => {
    fetchDepartmentStaff();
  }, [fetchDepartmentStaff]);

  const getServiceColor = (service) => {
    const colors = {
      Spa: 'bg-pink-100 text-pink-800',
      Yoga: 'bg-green-100 text-green-800',
      Salon: 'bg-purple-100 text-purple-800'
    };
    return colors[service] || 'bg-gray-100 text-gray-800';
  };

  const normalizeBookingStatus = (status) => {
    if (status === 'completed') return 'completed';
    return 'pending';
  };

  const getBookingServiceDetail = (booking) => {
    const category = normalizeCategory(booking?.category);
    if (category === 'Yoga') return null;
    if (category !== 'Spa' && category !== 'Salon') return booking?.details || null;

    const rawDetails = String(booking?.details || '').trim();
    if (!rawDetails) return null;
    const match = rawDetails.match(/service selected:\s*(.+)$/i);
    const selectedService = match && match[1] ? String(match[1]).trim() : '';
    if (!selectedService) return null;
    return selectedService;
  };

  const handleBookingSuccess = () => {
    fetchBookings();
  };

  const getSelectedDateValue = () => getDateInputValue(selectedDate);
  const getCurrentISTMinutes = () => {
    const now = new Date();
    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    const istNow = new Date(now.getTime() + istOffsetMs);
    return (istNow.getUTCHours() * 60) + istNow.getUTCMinutes();
  };

  const getBookingsForSelectedDate = useCallback(() => {
    if (!selectedDate || !selectedService) return [];
    return bookings.filter((booking) => {
      const bookingDate = booking.metadata?.booking_date;
      return bookingDate === getSelectedDateValue();
    });
  }, [bookings, selectedDate, selectedService]);

  const generateTimeSlotsFromWorkingHours = useCallback((specificSettings = null) => {
    const settings = specificSettings || slotSettings;
    const defaultHours = {
      Spa: { start_time: '09:00', end_time: '20:00', duration_minutes: 60, capacity: 1 },
      Yoga: { start_time: '07:00', end_time: '09:00', duration_minutes: 60, capacity: 1 },
      Salon: { start_time: '09:00', end_time: '20:00', duration_minutes: 60, capacity: 1 }
    };
    const canonical = normalizeCategory(selectedService) || 'Spa';
    const workingHours = settings?.workingHours || defaultHours[canonical] || defaultHours.Spa;
    if (!workingHours?.start_time || !workingHours?.end_time) return [];

    const [startHour, startMinute] = String(workingHours.start_time).split(':').map(Number);
    const [endHour, endMinute] = String(workingHours.end_time).split(':').map(Number);
    const durationMinutes = Math.max(15, Number(workingHours.duration_minutes) || 60);
    const startTotal = (startHour * 60) + startMinute;
    const endTotal = (endHour * 60) + endMinute;
    const slots = [];

    for (let current = startTotal; current + durationMinutes <= endTotal; current += durationMinutes) {
      const end = current + durationMinutes;
      const sh = Math.floor(current / 60);
      const sm = current % 60;
      const eh = Math.floor(end / 60);
      const em = end % 60;
      const id = `${String(sh).padStart(2, '0')}:${String(sm).padStart(2, '0')}`;
      const title = new Date(2000, 0, 1, sh, sm).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const to = new Date(2000, 0, 1, eh, em).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      slots.push({ id, title, description: `${title} - ${to}` });
    }

    return slots.filter((slot) => {
      const slotRule = settings.timeSlots?.[slot.id];
      return !slotRule || slotRule.enabled !== false;
    });
  }, [slotSettings, selectedService]);

  const fetchEditableSlots = useCallback(async (category, date, currentTime = null) => {
    if (!category || !date) {
      setEditSlots([]);
      return;
    }
    try {
      // First fetch settings for THIS specific category (might be different from current view)
      const catKey = normalizeCategory(category);
      const settingsResponse = await api.get(`/api/slots/${catKey}`);
      const categorySettings = {
        timeSlots: settingsResponse.data?.timeSlots || {},
        workingHours: settingsResponse.data?.workingHours || null
      };

      const allSlots = generateTimeSlotsFromWorkingHours(categorySettings);
      const response = await api.get('/api/roomservice/available-slots', { 
        params: { category: catKey, date, source: 'admin' } 
      });
      const availableIds = new Set((Array.isArray(response.data) ? response.data : []).map(s => s.id));
      
      const normalizedSlots = allSlots.map((slot) => {
        const isAvailable = availableIds.has(slot.id);
        const isCurrent = slot.id === currentTime;
        let label = slot.description || slot.title || slot.id;
        
        if (isCurrent) {
          label += ' (Current)';
        } else if (isAvailable) {
          label += ' (Available)';
        } else {
          label += ' (Unavailable)';
        }
        
        return { id: slot.id, label, disabled: !isAvailable && !isCurrent };
      });
      
      // If currentTime is not in allSlots (e.g. customized slot), add it
      if (currentTime && !normalizedSlots.some((slot) => slot.id === currentTime)) {
        normalizedSlots.unshift({ id: currentTime, label: `${currentTime} (Current)`, disabled: false });
      }
      
      setEditSlots(normalizedSlots);
    } catch (error) {
      console.error('Failed to fetch editable slots:', error);
      setEditSlots([]);
    }
  }, [generateTimeSlotsFromWorkingHours]);

  // If user has no access to booking services, show message
  if (SERVICES.length === 0 && (user?.role === 'department')) {
    return (
      <Layout>
        <div className="text-center py-12">
          <FiCalendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500">You don't have access to booking services</p>
        </div>
      </Layout>
    );
  }

  if (loading && bookings.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  const selectedDateBookings = getBookingsForSelectedDate();
  const getStaffForCategory = (category) => {
    const mappedDepartment = getDepartmentStaffDepartmentForBookingCategory(category);
    return departmentStaff.filter((staff) => staff.is_active && staff.department === mappedDepartment);
  };

  const openEditBooking = (booking) => {
    const defaultDate = booking.metadata?.booking_date || '';
    const defaultTime = booking.metadata?.booking_time || '';
    const defaultWorkDoneBy = booking.metadata?.work_done_by || booking.metadata?.assigned_staff_name || booking.metadata?.completed_by_staff_name || '';
    setEditingBooking(booking);
    setEditForm({
      booking_date: defaultDate,
      booking_time: defaultTime,
      work_done_by: defaultWorkDoneBy
    });
    fetchEditableSlots(booking.category, defaultDate, defaultTime);
  };

  const saveBookingEdit = async () => {
    if (!editingBooking) return;
    if (!editForm.booking_date || !editForm.booking_time) {
      alert('Please select booking date and slot.');
      return;
    }
    try {
      setSavingEdit(true);
      await api.put(`/api/roomservice/request/${editingBooking.id}/booking`, {
        booking_date: editForm.booking_date,
        booking_time: editForm.booking_time,
        work_done_by: editForm.work_done_by || null
      });
      setEditingBooking(null);
      setEditSlots([]);
      await fetchBookings();
    } catch (error) {
      console.error('Failed to update booking:', error);
      alert(error.response?.data?.error || 'Failed to update booking');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      setDeletingBookingId(bookingId);
      // Wait, is there a delete booking API?
      // Requests can be deleted using /api/roomservice/request/:id
      await api.delete(`/api/roomservice/request/${bookingId}`);
      await fetchBookings();
    } catch (error) {
      console.error('Failed to delete booking:', error);
      alert(error.response?.data?.error || 'Failed to delete booking');
    } finally {
      setDeletingBookingId(null);
    }
  };
  const bookingsByTime = selectedDateBookings.reduce((acc, booking) => {
    const key = booking.metadata?.booking_time;
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(booking);
    return acc;
  }, {});
  const availableTimeSlots = generateTimeSlotsFromWorkingHours();

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-1 lg:py-2">
        <div className="space-y-4">
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
            <div>
              <h1 className="text-xl sm:text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Activity Bookings</h1>
              <p className="text-sm text-gray-500 mt-1">Manage upcoming bookings with 7-day quick view and slot-level status.</p>
            </div>

            {/* Service pill buttons — scroll horizontally on mobile */}
            <div className="-mx-1 overflow-x-auto pb-1">
              <div className="flex items-center gap-2 px-1 min-w-max flex-nowrap">
                {SERVICES.map((service) => (
                  <button
                    key={service}
                    onClick={() => setSelectedService(service)}
                    className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors flex-shrink-0 ${
                      selectedService === service
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {service}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
            {(user?.role === 'admin' || user?.role === 'manager' || user?.role === 'department') && (
              <button
                onClick={() => setShowBookingForm(true)}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center font-semibold"
              >
                <FiPlus className="mr-2" />
                New Booking
              </button>
            )}
          </div>
        </div>

        <InteractiveCalendar 
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          bookingsData={bookings.filter(b => b.category === selectedService)}
          serviceName={selectedService}
        >

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden w-full">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {selectedService || 'Service'} Slots for {formatDate(selectedDate, 'EEEE, MMMM d, yyyy')}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {selectedDateBookings.length} booking{selectedDateBookings.length === 1 ? '' : 's'} on selected day
            </p>
          </div>

          {availableTimeSlots.length === 0 ? (
            <div className="text-center py-12">
              <FiCalendar className="mx-auto h-10 w-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-500">No slots configured in Slot Management for this service</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {availableTimeSlots.map((slot) => {
                const slotBookings = bookingsByTime[slot.id] || [];
                const capacity = Number(slotSettings.workingHours?.capacity) || 1;
                const bookedPeopleCount = slotBookings.reduce((sum, b) => {
                  return sum + Number(b.metadata?.num_people || b.metadata?.number_of_people || 1);
                }, 0);
                const isFull = bookedPeopleCount >= capacity;
                const isPartiallyBooked = bookedPeopleCount > 0 && !isFull;
                const hasBookings = slotBookings.length > 0;
                return (
                  <div key={slot.id} className="p-4 sm:p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{slot.description}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{slot.id}</p>
                      </div>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                          isFull ? 'bg-red-100 text-red-800' : isPartiallyBooked ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {isFull ? 'Full' : 'Available'} ({bookedPeopleCount}/{capacity})
                      </span>
                    </div>

                    {hasBookings && (
                      <div className="mt-3 space-y-2">
                        {slotBookings.map((booking) => {
                          const normalizedStatus = normalizeBookingStatus(booking.status);
                          return (
                            <div key={booking.id} className="p-3 border border-gray-200 rounded-lg bg-gray-50/60">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getServiceColor(booking.category)}`}>
                                  {booking.category}
                                </span>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                  normalizedStatus === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {normalizedStatus === 'completed' ? 'Completed' : 'Pending'}
                                </span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="flex items-start space-x-2">
                                  <FiUser className="h-4 w-4 text-gray-400 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{booking.guest_name || 'Unknown Guest'}</p>
                                    {booking.room_no && <p className="text-xs text-gray-500">Room {booking.room_no}</p>}
                                    {booking.metadata?.num_people && (
                                      <p className="text-xs text-gray-600 font-medium">People: {booking.metadata.num_people}</p>
                                    )}
                                    {booking.phone && (
                                      <p className="text-xs text-gray-500 flex items-center mt-0.5">
                                        <FiPhone className="h-3 w-3 mr-1" />
                                        {booking.phone}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500">
                                  <p>Created: {formatDateTime(booking.created_at)}</p>
                                  <p className="mt-0.5">ID: {booking.id.substring(0, 8)}</p>
                                  <p className="mt-0.5">
                                    Work Done By: {booking.metadata?.work_done_by || booking.metadata?.assigned_staff_name || booking.metadata?.completed_by_staff_name || 'Not assigned'}
                                  </p>
                                </div>
                              </div>
                              {(() => {
                                const serviceDetail = getBookingServiceDetail(booking);
                                if (!serviceDetail) return null;
                                const isWellnessService = ['Spa', 'Salon'].includes(normalizeCategory(booking.category));
                                return (
                                  <p className="text-xs text-gray-600 mt-2 bg-white border border-gray-200 rounded-md p-2">
                                    {isWellnessService ? <strong>{serviceDetail}</strong> : serviceDetail}
                                  </p>
                                );
                              })()}
                              <div className="mt-2 flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => openEditBooking(booking)}
                                  className="px-2.5 py-1.5 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                >
                                  Assign / Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteBooking(booking.id)}
                                  disabled={deletingBookingId === booking.id}
                                  className="px-2.5 py-1.5 rounded-md border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                                >
                                  {deletingBookingId === booking.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </InteractiveCalendar>

        <BookingForm
          isOpen={showBookingForm}
          onClose={() => setShowBookingForm(false)}
          onSuccess={handleBookingSuccess}
          category={selectedService || null}
          availableServices={SERVICES}
          lockCategory={shouldLockBookingService}
        />

        {editingBooking && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="w-full max-w-lg bg-white rounded-xl border border-gray-200 shadow-2xl">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900">Edit Booking</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {editingBooking.category} - {editingBooking.guest_name || 'Guest'}
                </p>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date</label>
                  <input
                    type="date"
                    value={editForm.booking_date}
                    onChange={(e) => {
                      const date = e.target.value;
                      setEditForm((prev) => ({ ...prev, booking_date: date, booking_time: '' }));
                      fetchEditableSlots(editingBooking.category, date, editingBooking.metadata?.booking_time || null);
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Time Slot</label>
                  <select
                    value={editForm.booking_time}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, booking_time: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Select slot</option>
                    {editSlots.map((slot) => (
                      <option key={slot.id} value={slot.id} disabled={slot.disabled}>{slot.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Work Done By</label>
                  <input
                    type="text"
                    value={editForm.work_done_by}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, work_done_by: e.target.value }))}
                    placeholder="Enter staff name or team (optional)"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => setEditingBooking(null)}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold"
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button
                  onClick={saveBookingEdit}
                  className="px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-semibold disabled:opacity-60"
                  disabled={savingEdit}
                >
                  {savingEdit ? 'Saving...' : 'Update Booking'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Bookings;

